/**
 * Costing Service
 * 
 * Handles inventory valuation calculations for different costing methods.
 * Implements Average Cost (Weighted Average) and FIFO methods.
 * 
 * @module services/costing-service
 */

import * as FIFOService from './fifo-service';

export type ValuationMethod = 'average-cost' | 'fifo' | 'standard-cost';

/**
 * Calculate weighted average cost when receiving new stock
 * 
 * Formula: (Current Value + New Purchase Value) / (Current Qty + New Qty)
 * 
 * @param currentQty - Current quantity in stock
 * @param currentAvgCost - Current average cost per unit
 * @param newQty - Quantity being received
 * @param newCost - Cost per unit of new stock
 * @returns New weighted average cost
 * 
 * @example
 * // You have 100 units at £10 each, receive 50 units at £12 each
 * const newAvg = calculateAverageCost(100, 10, 50, 12);
 * // Result: £10.67 = (1000 + 600) / 150
 */
export const calculateAverageCost = (
  currentQty: number,
  currentAvgCost: number,
  newQty: number,
  newCost: number
): number => {
  // Handle edge cases
  if (newQty <= 0) return currentAvgCost;
  if (currentQty <= 0) return newCost;
  
  const currentValue = currentQty * currentAvgCost;
  const newValue = newQty * newCost;
  const totalValue = currentValue + newValue;
  const totalQty = currentQty + newQty;
  
  return parseFloat((totalValue / totalQty).toFixed(4));
};

/**
 * Calculate Cost of Goods Sold (COGS) for a sale
 * 
 * For Average Cost method: COGS = Quantity × Current Average Cost
 * 
 * @param quantity - Quantity being sold
 * @param averageCost - Current average cost per unit
 * @returns Total cost of goods sold
 * 
 * @example
 * // Selling 20 units with average cost of £10.67
 * const cogs = calculateCOGS(20, 10.67);
 * // Result: £213.40
 */
export const calculateCOGS = (
  quantity: number,
  averageCost: number
): number => {
  if (quantity <= 0 || averageCost < 0) return 0;
  return parseFloat((quantity * averageCost).toFixed(2));
};

/**
 * Calculate profit margin percentage
 * 
 * Formula: ((Selling Price - Cost) / Selling Price) × 100
 * 
 * @param sellingPrice - Price per unit sold to customer
 * @param cost - Cost per unit (average cost, FIFO cost, or standard cost)
 * @returns Margin percentage (0-100)
 * 
 * @example
 * // Selling at £15, cost is £10
 * const margin = calculateMargin(15, 10);
 * // Result: 33.33%
 */
export const calculateMargin = (
  sellingPrice: number,
  cost: number
): number => {
  if (sellingPrice <= 0) return 0;
  const margin = ((sellingPrice - cost) / sellingPrice) * 100;
  return parseFloat(margin.toFixed(2));
};

/**
 * Calculate markup percentage
 * 
 * Formula: ((Selling Price - Cost) / Cost) × 100
 * 
 * @param sellingPrice - Price per unit sold to customer
 * @param cost - Cost per unit
 * @returns Markup percentage
 * 
 * @example
 * // Selling at £15, cost is £10
 * const markup = calculateMarkup(15, 10);
 * // Result: 50%
 */
export const calculateMarkup = (
  sellingPrice: number,
  cost: number
): number => {
  if (cost <= 0) return 0;
  const markup = ((sellingPrice - cost) / cost) * 100;
  return parseFloat(markup.toFixed(2));
};

/**
 * Calculate inventory value for a stock item
 * 
 * @param quantity - Current quantity in stock
 * @param cost - Cost per unit (depends on valuation method)
 * @returns Total inventory value
 */
export const calculateInventoryValue = (
  quantity: number,
  cost: number
): number => {
  if (quantity <= 0 || cost < 0) return 0;
  return parseFloat((quantity * cost).toFixed(2));
};

/**
 * Adjust average cost for stock adjustments (write-offs, corrections)
 * 
 * When quantity is adjusted without a purchase, the average cost remains the same.
 * This function is for documentation purposes - no calculation needed.
 * 
 * @param currentAvgCost - Current average cost
 * @returns Same average cost (no change)
 */
export const adjustAverageCostForAdjustment = (
  currentAvgCost: number
): number => {
  // For stock adjustments, write-offs, and corrections,
  // the average cost doesn't change - we just adjust quantity
  return currentAvgCost;
};

/**
 * Calculate average cost after a stock transfer
 * 
 * For transfers between locations, the average cost moves with the stock.
 * This function is for documentation purposes.
 * 
 * @param currentAvgCost - Current average cost
 * @returns Same average cost (transfers don't change cost)
 */
export const transferAverageCost = (
  currentAvgCost: number
): number => {
  // Transfers between locations maintain the same average cost
  return currentAvgCost;
};

// ============================================================================
// FIFO COSTING (IMPLEMENTED)
// ============================================================================

/**
 * Calculate COGS using FIFO method
 * 
 * FIFO (First In, First Out) consumes from the oldest cost layers first.
 * See fifo-service.ts for detailed implementation.
 * 
 * @param stockCode - Stock item code
 * @param warehouseCode - Warehouse code
 * @param quantity - Quantity being sold
 * @returns FIFO calculation result
 * 
 * @example
 * // Calculate FIFO cost for selling 50 units
 * const result = calculateFIFOCOGS('ITEM001', 'WH01', 50);
 * // Result includes total cost, average cost, and layers used
 */
export const calculateFIFOCOGS = (
  stockCode: string,
  warehouseCode: string,
  quantity: number
) => {
  return FIFOService.calculateFIFOCost(stockCode, warehouseCode, quantity);
};

/**
 * Add a cost layer when receiving stock (FIFO method)
 * 
 * @param stockCode - Stock item code
 * @param warehouseCode - Warehouse code
 * @param locationCode - Location code
 * @param quantity - Quantity received
 * @param unitCost - Cost per unit
 * @param reference - Reference (PO number, etc.)
 * @returns The created cost layer
 */
export const addFIFOCostLayer = (
  stockCode: string,
  warehouseCode: string,
  locationCode: string | undefined,
  quantity: number,
  unitCost: number,
  reference?: string
) => {
  return FIFOService.addCostLayer(
    stockCode,
    warehouseCode,
    locationCode,
    quantity,
    unitCost,
    reference
  );
};

/**
 * Get current FIFO cost (cost of next unit to be sold)
 * 
 * @param stockCode - Stock item code
 * @param warehouseCode - Warehouse code
 * @returns Unit cost of oldest layer
 */
export const getCurrentFIFOCost = (
  stockCode: string,
  warehouseCode: string
): number => {
  return FIFOService.getCurrentFIFOCost(stockCode, warehouseCode);
};

/**
 * Get weighted average cost across all FIFO layers
 * 
 * @param stockCode - Stock item code
 * @param warehouseCode - Warehouse code
 * @returns Weighted average cost
 */
export const getFIFOWeightedAverage = (
  stockCode: string,
  warehouseCode: string
): number => {
  return FIFOService.getWeightedAverageCost(stockCode, warehouseCode);
};

/**
 * Get cost layer summary for FIFO
 * 
 * @param stockCode - Stock item code
 * @param warehouseCode - Warehouse code
 * @returns Cost layer summary
 */
export const getFIFOCostLayerSummary = (
  stockCode: string,
  warehouseCode: string
) => {
  return FIFOService.getCostLayerSummary(stockCode, warehouseCode);
};

// ============================================================================
// STANDARD COSTING (IMPLEMENTED)
// ============================================================================

export interface CostVariance {
  stockCode: string;
  transactionDate: Date;
  transactionType: 'purchase' | 'production' | 'adjustment';
  quantity: number;
  standardCost: number;
  actualCost: number;
  variance: number;
  varianceType: 'favorable' | 'unfavorable' | 'none';
  variancePercentage: number;
  reference?: string;
}

/**
 * Calculate cost variance for Standard Cost method
 * 
 * Variance = Actual Cost - Standard Cost
 * - Positive variance = Unfavorable (paid more than standard)
 * - Negative variance = Favorable (paid less than standard)
 * 
 * @param actualCost - Actual cost paid per unit
 * @param standardCost - Predetermined standard cost per unit
 * @param quantity - Quantity purchased/produced
 * @returns Variance details
 * 
 * @example
 * // Standard cost is £15, actual cost is £18, purchased 100 units
 * const variance = calculateCostVariance(18, 15, 100);
 * // Result: {
 * //   unitVariance: 3.00,
 * //   totalVariance: 300.00,
 * //   varianceType: 'unfavorable',
 * //   variancePercentage: 20.00
 * // }
 */
export const calculateCostVariance = (
  actualCost: number,
  standardCost: number,
  quantity: number
): {
  unitVariance: number;
  totalVariance: number;
  varianceType: 'favorable' | 'unfavorable' | 'none';
  variancePercentage: number;
} => {
  if (standardCost <= 0) {
    return {
      unitVariance: 0,
      totalVariance: 0,
      varianceType: 'none',
      variancePercentage: 0,
    };
  }

  const unitVariance = parseFloat((actualCost - standardCost).toFixed(4));
  const totalVariance = parseFloat((unitVariance * quantity).toFixed(2));
  const variancePercentage = parseFloat(
    ((unitVariance / standardCost) * 100).toFixed(2)
  );

  let varianceType: 'favorable' | 'unfavorable' | 'none';
  if (unitVariance > 0.01) {
    varianceType = 'unfavorable'; // Paid more than standard
  } else if (unitVariance < -0.01) {
    varianceType = 'favorable'; // Paid less than standard
  } else {
    varianceType = 'none'; // Within tolerance
  }

  return {
    unitVariance,
    totalVariance,
    varianceType,
    variancePercentage,
  };
};

/**
 * Calculate COGS using Standard Cost method
 * 
 * For Standard Cost, COGS always uses the predetermined standard cost,
 * regardless of actual purchase costs.
 * 
 * @param quantity - Quantity being sold
 * @param standardCost - Predetermined standard cost per unit
 * @returns Total cost of goods sold at standard cost
 * 
 * @example
 * // Selling 20 units with standard cost of £15
 * const cogs = calculateStandardCOGS(20, 15);
 * // Result: £300.00 (always uses standard cost)
 */
export const calculateStandardCOGS = (
  quantity: number,
  standardCost: number
): number => {
  if (quantity <= 0 || standardCost < 0) return 0;
  return parseFloat((quantity * standardCost).toFixed(2));
};

/**
 * Create a cost variance record for tracking
 * 
 * @param stockCode - Stock item code
 * @param transactionType - Type of transaction
 * @param quantity - Quantity involved
 * @param standardCost - Standard cost per unit
 * @param actualCost - Actual cost per unit
 * @param reference - Transaction reference
 * @returns Cost variance record
 */
export const createCostVarianceRecord = (
  stockCode: string,
  transactionType: 'purchase' | 'production' | 'adjustment',
  quantity: number,
  standardCost: number,
  actualCost: number,
  reference?: string
): CostVariance => {
  const variance = calculateCostVariance(actualCost, standardCost, quantity);

  return {
    stockCode,
    transactionDate: new Date(),
    transactionType,
    quantity,
    standardCost,
    actualCost,
    variance: variance.totalVariance,
    varianceType: variance.varianceType,
    variancePercentage: variance.variancePercentage,
    reference,
  };
};

/**
 * Calculate inventory value using Standard Cost method
 * 
 * @param quantity - Current quantity in stock
 * @param standardCost - Standard cost per unit
 * @returns Total inventory value at standard cost
 */
export const calculateStandardInventoryValue = (
  quantity: number,
  standardCost: number
): number => {
  if (quantity <= 0 || standardCost < 0) return 0;
  return parseFloat((quantity * standardCost).toFixed(2));
};

/**
 * Determine if standard cost needs review based on variance trends
 * 
 * @param variances - Array of recent cost variances
 * @param thresholdPercentage - Threshold for review (default: 10%)
 * @returns Whether standard cost should be reviewed
 */
export const shouldReviewStandardCost = (
  variances: CostVariance[],
  thresholdPercentage: number = 10
): {
  shouldReview: boolean;
  reason: string;
  averageVariance: number;
  unfavorableCount: number;
  favorableCount: number;
} => {
  if (variances.length === 0) {
    return {
      shouldReview: false,
      reason: 'No variance data available',
      averageVariance: 0,
      unfavorableCount: 0,
      favorableCount: 0,
    };
  }

  const totalVariance = variances.reduce(
    (sum, v) => sum + v.variancePercentage,
    0
  );
  const averageVariance = totalVariance / variances.length;

  const unfavorableCount = variances.filter(
    (v) => v.varianceType === 'unfavorable'
  ).length;
  const favorableCount = variances.filter(
    (v) => v.varianceType === 'favorable'
  ).length;

  let shouldReview = false;
  let reason = 'Standard cost is within acceptable range';

  // Check if average variance exceeds threshold
  if (Math.abs(averageVariance) > thresholdPercentage) {
    shouldReview = true;
    reason = `Average variance (${averageVariance.toFixed(
      2
    )}%) exceeds threshold (${thresholdPercentage}%)`;
  }

  // Check if consistently unfavorable
  if (unfavorableCount > variances.length * 0.7) {
    shouldReview = true;
    reason = `${unfavorableCount} of ${variances.length} transactions are unfavorable`;
  }

  // Check if consistently favorable (might be too high)
  if (favorableCount > variances.length * 0.7) {
    shouldReview = true;
    reason = `${favorableCount} of ${variances.length} transactions are favorable - standard may be too high`;
  }

  return {
    shouldReview,
    reason,
    averageVariance: parseFloat(averageVariance.toFixed(2)),
    unfavorableCount,
    favorableCount,
  };
};

/**
 * Calculate recommended standard cost based on recent actual costs
 * 
 * @param recentActualCosts - Array of recent actual costs
 * @param method - Calculation method ('average' | 'median' | 'weighted')
 * @returns Recommended standard cost
 */
export const calculateRecommendedStandardCost = (
  recentActualCosts: Array<{ cost: number; quantity: number; date: Date }>,
  method: 'average' | 'median' | 'weighted' = 'weighted'
): number => {
  if (recentActualCosts.length === 0) return 0;

  if (method === 'average') {
    const total = recentActualCosts.reduce((sum, item) => sum + item.cost, 0);
    return parseFloat((total / recentActualCosts.length).toFixed(4));
  }

  if (method === 'median') {
    const sorted = [...recentActualCosts]
      .map((item) => item.cost)
      .sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 === 0
      ? parseFloat(((sorted[mid - 1] + sorted[mid]) / 2).toFixed(4))
      : sorted[mid];
  }

  // Weighted average (default) - more recent costs have more weight
  const totalWeight = recentActualCosts.reduce(
    (sum, item) => sum + item.quantity,
    0
  );
  const weightedSum = recentActualCosts.reduce(
    (sum, item) => sum + item.cost * item.quantity,
    0
  );
  return parseFloat((weightedSum / totalWeight).toFixed(4));
};
